// small polyfill for requestIdleCallback
const requestIdleCallback = typeof window.requestIdleCallback == 'function' ? window.requestIdleCallback : setTimeout;

function formatMoneyWithCurrency(amount, locale = 'en-US', currency = 'USD') {
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  });

  return formatter.format(amount / 100);
}

function makeCancelable(promise) {
  let cancel;

  const cancelPromise = new Promise((resolve) => {
    cancel = (reason) => resolve({ canceled: true, reason });
  });

  return {
    promise: Promise.race([promise, cancelPromise]),
    cancel,
  };
}

function waitForAnimationEnd(elements, options = { subtree: true }) {
  const animations = Array.isArray(elements)
    ? elements.flatMap((element) => element.getAnimations(options))
    : elements.getAnimations(options);
  const animationPromises = animations.reduce((acc, animation) => {
    if (animation.timeline instanceof DocumentTimeline) {
      acc.push(animation.finished);
    }

    return acc;
  }, []);

  return makeCancelable(Promise.allSettled(animationPromises));
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function checkForReducedMotion() {
  let prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  localStorage.setItem('prefersReducedMotion', prefersReducedMotion);
  document.documentElement.classList.toggle('reduced-motion', prefersReducedMotion);
}

checkForReducedMotion();

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function focus(element) {
  if (element && element.focus) {
    element.focus();
  }
}

// previous implementation from Dawn theme not account for programmatical focus change,
// or focus change by screen readers
// "strict" option tries to always focus back into the container in those cases
function trapFocus(container, elementToFocus = container, strict = false) {
  if (!container) return;
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (!strict && event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
    const target = event.target;

    if (container.contains(target)) {
      return;
    }

    focus(first);
  };

  trapFocusHandlers.focusout = function (e) {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
    if (strict) {
      const relatedTarget = e.relatedTarget;

      if (relatedTarget === null) {
        return;
      }

      if (!container.contains(relatedTarget)) {
        focus(first);
      }
    }
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      focus(first);
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      focus(last);
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  focus(elementToFocus);

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.announcementRegion = this.querySelector('.js-quantity-announcement');
    this.translations = this.loadTranslations();
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
    this.announceQuantityChange(this.input.value);
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) {
      const changeType = event.target.name === 'plus' ? 'increase' : 'decrease';
      this.input.dispatchEvent(
        new CustomEvent('change', {
          bubbles: true,
          detail: { type: changeType },
        })
      );
      this.announceQuantityChange(this.input.value);
    }

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      const reachMin = parseInt(value) <= parseInt(this.input.min);
      if (buttonMinus) {
        buttonMinus.classList.toggle('disabled', reachMin);
        buttonMinus.setAttribute('aria-disabled', reachMin);
      }

      // avoid screen reader decreasing the input value below min
      if (reachMin) {
        this.input.value = parseInt(this.input.min);
      }
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      const reachMax = value >= max;
      if (buttonPlus) {
        buttonPlus.classList.toggle('disabled', value >= max);
        buttonPlus.setAttribute('aria-disabled', reachMax);
      }

      // avoid screen reader increasing the input value above max
      if (reachMax) {
        this.input.value = parseInt(this.input.max);
      }
    }
  }

  loadTranslations() {
    const translationsElement = document.querySelector('[data-quantity-translations]');

    return translationsElement ? JSON.parse(translationsElement.textContent) : {};
  }

  announceQuantityChange(newValue) {
    if (!this.announcementRegion || !this.translations) return;

    const min = this.input.min ? parseInt(this.input.min) : null;
    const max = this.input.max ? parseInt(this.input.max) : null;
    const value = parseInt(newValue);

    let messageTemplate = '';

    // Determine appropriate message template based on boundaries
    if (min !== null && value <= min && min) {
      messageTemplate = this.translations.quantity_at_minimum;
    } else if (max !== null && value >= max) {
      messageTemplate = this.translations.quantity_at_maximum;
    } else {
      messageTemplate = this.translations.quantity_updated;
    }

    const message = messageTemplate.replace('{{ quantity }}', value);
    this.announcementRegion.textContent = message;
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) => {
      if (!summary.parentElement?.classList.contains('menu-drawer-top-level')) {
        summary.addEventListener('click', this.onSummaryClick.bind(this));
      }
    });

    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));

    this.querySelectorAll('.menu-item--grandchild-link').forEach((link) => {
      link.addEventListener('click', (event) => {
        const detailsElement = event.currentTarget.closest('details');
        this.closeSubmenu(detailsElement);
        this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
        this.mainDetailsToggle.querySelector('summary')?.setAttribute('aria-expanded', false);
      });
    });
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 1024px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        if (
          detailsElement.classList.contains('menu-drawer-container') &&
          document.querySelector('.section-header').classList.contains('menu-open')
        ) {
          return;
        }
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');
    // when the submenu is open, and then click the close button in header
    // we want the exit transition to the left by adding a exit-left class
    // here I reset it when open the drawer again
    document
      .querySelectorAll('.mega-menu__section')
      .forEach((megaSection) => megaSection.classList.remove('exit-left'));

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');

    this.megaSection = document.querySelector('.mega-menu__section[aria-expanded="true"]');
    this.megaSectionWrapper = document.querySelector('.mega-menu__section-wrapper');
    if (this.megaSection) {
      // exit the submenu to the left
      this.megaSection.classList.add('exit-left');
      this.megaSection.setAttribute('aria-expanded', false);
      this.megaSectionWrapper.removeAttribute('open');
      this.megaSectionWrapper.removeAttribute('bg');
    }

    this.openMenu = document.querySelector('drawer-menu[open]');
    if (this.openMenu) {
      this.openMenu.removeAttribute('open');
    }

    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class SizeChart extends HTMLElement {
  constructor() {
    super();

    this.onUnitChange = this.onUnitChange.bind(this);
  }

  connectedCallback() {
    this.table = this.querySelector('.size-chart__table');
    this.unitInputs = Array.from(this.querySelectorAll('input[name^="unit-option"]'));

    if (!this.table || this.unitInputs.length === 0) return;

    this.unitInputs.forEach((input) => input.addEventListener('change', this.onUnitChange));

    const checked = this.unitInputs.find((input) => input.checked) || this.unitInputs[0];
    this.applyUnit(checked?.value);
  }

  disconnectedCallback() {
    if (!this.unitInputs) return;
    this.unitInputs.forEach((input) => input.removeEventListener('change', this.onUnitChange));
  }

  onUnitChange(event) {
    this.applyUnit(event.target.value);
  }

  applyUnit(unit) {
    if (!this.table) return;

    const normalizedUnit = String(unit || '').toUpperCase();

    if (normalizedUnit === 'US' || normalizedUnit === 'EU') {
      const showEu = normalizedUnit === 'EU';
      this.classList.toggle('eu-active', showEu);
      this.classList.toggle('us-active', !showEu);
      this.querySelectorAll('.size-chart__col--eu').forEach((el) =>
        el.setAttribute('aria-hidden', showEu ? 'false' : 'true')
      );
      this.querySelectorAll('.size-chart__col--us').forEach((el) =>
        el.setAttribute('aria-hidden', showEu ? 'true' : 'false')
      );
    } else {
      const useCm = normalizedUnit === 'CM';
      this.classList.toggle('cm-active', useCm);
      this.classList.toggle('inches-active', !useCm);
      this.table
        .querySelectorAll('.size-chart__inches-value')
        .forEach((el) => el.setAttribute('aria-hidden', useCm ? 'true' : 'false'));
      this.table
        .querySelectorAll('.size-chart__cm-value')
        .forEach((el) => el.setAttribute('aria-hidden', useCm ? 'false' : 'true'));
      this.querySelectorAll('.size-chart__note--inches').forEach((el) =>
        el.setAttribute('aria-hidden', useCm ? 'true' : 'false')
      );
      this.querySelectorAll('.size-chart__note--cm').forEach((el) =>
        el.setAttribute('aria-hidden', useCm ? 'false' : 'true')
      );
    }
  }
}
customElements.define('size-chart', SizeChart);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    this.poster = this.querySelector('.deferred-media__poster-button');

    // this 'posterButton' must be a real button element
    this.posterButton = this.querySelector('.deferred-media__poster');
    this.video = this.querySelector('video');
    this.videoThumbnail = this.querySelector('.video-section__image');

    if (!this.poster && !this.posterButton && !this.video && !this.videoThumbnail) return;

    // Checking for controls attribute to prevent double triggering on videos with controls
    if (this.video && !this.video.hasAttribute('controls')) {
      this.video.addEventListener('click', this.loadContent.bind(this));
    }

    if (this.video) {
      this.video.addEventListener('play', this.handleVideoPlay.bind(this));
      this.video.addEventListener('pause', this.handleVideoPause.bind(this));
      this.video.addEventListener('ended', this.handleVideoPause.bind(this));
    }

    if (this.posterButton) {
      this.setupAccessibility();

      // Some screen readers don't trigger keydown events when 'Space' or 'Enter'
      // However, because we're using a real button,
      // click event will be triggered for all cases: "click", 'Space', 'Enter'
      this.posterButton.addEventListener('click', this.loadContent.bind(this));
    }

    this.prefersReducedMotion();
    this.updatePlayPauseIcons();
  }

  handleVideoPlay() {
    this.classList.add('playing');
    this.updateAriaLabel();
    this.updatePlayPauseIcons();
  }

  handleVideoPause() {
    this.classList.remove('playing');
    this.updateAriaLabel();
    this.updatePlayPauseIcons();
  }

  setupAccessibility() {
    if (!this.posterButton) return;

    this.updateAriaLabel();
  }

  loadContent(focus = true) {
    this.classList.add('thumbnail-hidden');

    if (!this.getAttribute('loaded')) {
      const template = this.querySelector('template');
      const model = template && template.content.querySelector('model-viewer');
      if (model) {
        this.appendChild(model.cloneNode(true));
        this.setAttribute('loaded', true);
      }
    }

    const deferredElement = this.querySelector('video, model-viewer, iframe');
    if (!deferredElement) return;

    if (deferredElement.nodeName == 'VIDEO') {
      deferredElement.classList.remove('hidden');
      if (this.classList.contains('playing')) {
        this.classList.remove('playing');
        deferredElement.pause();
      } else {
        this.classList.add('playing');
        deferredElement.play();
      }

      this.updateAriaLabel();
      this.updatePlayPauseIcons();
    }

    // Update the state for screen readers
    if (this.videoThumbnail) {
      if (this.videoThumbnail.getAttribute('alt')) {
        this.videoThumbnail.setAttribute('alt', this.videoThumbnail.getAttribute('alt').replace('Load video: ', ''));
      }
      this.videoThumbnail.setAttribute('aria-hidden', true);
      this.videoThumbnail.style.zIndex = -1;
    }

    if (focus) deferredElement.focus();
  }

  updateAriaLabel() {
    if (!this.posterButton) return;

    const videoAlt = this.posterButton.getAttribute('data-alt');
    const isPlaying = this.classList.contains('playing');

    // Check for localized labels from data attributes
    const playLabel = this.getAttribute('data-play-label');
    const pauseLabel = this.getAttribute('data-pause-label');

    let newLabel;
    if (playLabel && pauseLabel) {
      newLabel = isPlaying ? `${pauseLabel} ${videoAlt}` : `${playLabel} ${videoAlt}`;
    } else {
      newLabel = isPlaying ? `Pause video: ${videoAlt}` : `Play video: ${videoAlt}`;
    }

    // Clear and reset to ensure announcement
    const currentLabel = this.posterButton.getAttribute('aria-label');
    if (currentLabel !== newLabel) {
      this.posterButton.setAttribute('aria-label', newLabel);
    }
  }

  updatePlayPauseIcons() {
    if (!this.poster) return;

    const playIcon = this.poster.querySelector('.play');
    const pauseIcon = this.poster.querySelector('.pause');
    const isPlaying = this.classList.contains('playing');

    if (isPlaying) {
      this.poster.classList.remove('paused');
      if (playIcon) playIcon.style.display = 'none';
      if (pauseIcon) pauseIcon.style.display = 'block';
    } else {
      this.poster.classList.add('paused');
      if (playIcon) playIcon.style.display = 'block';
      if (pauseIcon) pauseIcon.style.display = 'none';
    }
  }

  prefersReducedMotion() {
    document.addEventListener('DOMContentLoaded', () => {
      const prefersReducedMotion = localStorage.getItem('prefersReducedMotion') === 'true';
      const animatedElements = document.querySelectorAll('.animated-content');
      const staticElements = document.querySelectorAll('.static-content');

      if (prefersReducedMotion) {
        animatedElements.forEach((el) => (el.style.display = 'none'));
        staticElements.forEach((el) => (el.style.display = 'block'));

        if (this.posterButton) {
          this.posterButton.setAttribute('tabindex', '-1');
        }
      } else {
        animatedElements.forEach((el) => (el.style.display = 'block'));
        staticElements.forEach((el) => (el.style.display = 'none'));
      }
    });
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT') {
      const newSelectedOption = Array.from(target.options).find((option) => option.value === value);

      if (!newSelectedOption) {
        return;
      }

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');

      const swatchValue = newSelectedOption.dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        newSelectedOption.dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.options[target.selectedIndex] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CollectionNavigation extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('DOMContentLoaded', this.init.bind(this));
    if (document.readyState === 'interactive' || document.readyState === 'complete') {
      this.init();
    }
  }

  init() {
    const currentItem = this.querySelector('.current-collection');

    if (!currentItem) return;

    setTimeout(() => {
      currentItem.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }, 100);
  }
}

customElements.define('collection-navigation', CollectionNavigation);

class BlogLandingFilters extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    const currentItem = this.querySelector('.blog-landing__filter-button--active');

    if (!currentItem) return;

    // Skip scrolling for the All articles button
    const firstButton = this.querySelector('.blog-landing__filter-button');
    if (currentItem === firstButton) return;

    setTimeout(() => {
      currentItem.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }, 100);
  }
}

customElements.define('blog-landing-filters', BlogLandingFilters);

class VariantImageSwitcher extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const inputs = this.querySelectorAll('input[type="radio"]');
    if (!inputs.length) return;

    const groupId = `swatch-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    // The same product can appear twice on a page, so the Liquid ids repeat; give each input its own id.
    inputs.forEach((input, index) => {
      const label = this.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      input.name = groupId;
      input.id = `${groupId}-${index}`;
      if (label) label.htmlFor = input.id;
    });

    if (!Array.from(inputs).some((i) => i.checked)) {
      inputs[0].checked = true;
    }

    this.addEventListener('click', (event) => this.handleClick(event));

    window.addEventListener('pageshow', () => {
      const currentSelected = Array.from(inputs).find((input) => {
        return input.checked;
      });

      if (currentSelected) {
        this.handleSwatchClick(currentSelected);
      } else {
        inputs[0].click();
      }
    });
  }

  handleClick(event) {
    const clickedInput = event.target;

    // Ignore clicks on things that aren't radio buttons
    if (!(clickedInput instanceof HTMLInputElement) || clickedInput.type !== 'radio') {
      return;
    }

    this.handleSwatchClick(clickedInput);
  }

  /**
   * Handle swatch click event
   */
  handleSwatchClick(swatch) {
    const variantId = swatch.getAttribute('data-variant-id');
    const mediaId = swatch.getAttribute('data-media-id');
    const colorName = swatch.getAttribute('data-color-name');
    const productCard = this.findParentProductCard(this);

    if (!productCard) return;

    this.switchProductImage(productCard, variantId, mediaId);
    this.updateCardLinks(productCard, variantId);
    this.updateSelectedColor(colorName);

    publish(PUB_SUB_EVENTS.swatchVariantPickerChange, { selectedVariant: variantId });
  }

  /**
   * @param {HTMLElement} element - The starting element
   * @returns {HTMLElement|null} The product card element or null
   */
  findParentProductCard(element) {
    let current = element;
    while (current) {
      if (current.classList.contains('card-wrapper')) {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  }

  /**
   * @param {HTMLElement} productCard - The product card element
   * @param {string} variantId - The variant ID
   * @param {string} mediaId - The media ID (optional)
   */
  switchProductImage(productCard, variantId, mediaId) {
    const imagesContainer = productCard.querySelector('.variant-images-container');
    if (!imagesContainer) return;

    const currentImage = imagesContainer.querySelector('img.active');
    if (!currentImage) return;

    let targetImage;
    if (mediaId) {
      targetImage = imagesContainer.querySelector(`img[data-media-id="${mediaId}"]`);
    }

    if (!targetImage) {
      targetImage = imagesContainer.querySelector(`img[data-variant-id="${variantId}"]`);
    }

    if (!targetImage || targetImage === currentImage) return;

    currentImage.classList.remove('active');
    targetImage.classList.add('active');
  }

  updateCardLinks(productCard, variantId) {
    const links = productCard.querySelectorAll('a[href]');
    links.forEach((link) => {
      const url = new URL(link.href);
      if (!url.pathname.startsWith('/products/')) return;
      url.searchParams.set('variant', variantId);
      link.href = url.toString();
    });
  }

  updateSelectedColor(value) {
    const selectedColor = this.querySelector('[data-selected-color]');
    if (selectedColor) selectedColor.innerHTML = value;
  }
}

customElements.define('swatch-variant-picker', VariantImageSwitcher);

class AccountTabs extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('DOMContentLoaded', this.init.bind(this));
    if (document.readyState === 'interactive' || document.readyState === 'complete') {
      this.init();
    }
  }

  init() {
    this.tabButtons = this.querySelectorAll('.tab-btn');
    this.tabContents = this.querySelectorAll('.tab-content');

    this.tabButtons.forEach((button) => {
      button.addEventListener('click', this.handleTabClick.bind(this));
    });

    if (!this.querySelector('.tab-btn.active')) {
      this.activateTab(this.tabButtons[0], this.tabContents[0]);
    }
  }

  handleTabClick(event) {
    const clickedButton = event.currentTarget;
    const tabId = clickedButton.getAttribute('data-tab');
    const tabContent = this.querySelector(`#${tabId}`);

    if (tabContent) {
      this.activateTab(clickedButton, tabContent);
    }
  }

  activateTab(button, content) {
    this.tabButtons.forEach((btn) => btn.classList.remove('active'));
    this.tabContents.forEach((content) => content.classList.remove('active'));

    button.classList.add('active');
    content.classList.add('active');
  }
}

customElements.define('account-tabs', AccountTabs);

class CustomerAddressForms extends HTMLElement {
  constructor() {
    super();
    this.addressNewButton = document.getElementById('addAddressButton');
    this.addressFormWrapper = document.getElementById('addressWrapper');
    this.newAddressWrapper = document.getElementById('AddAddress');
    this.editButtons = document.querySelectorAll('button[id^="EditFormButton_"]');
    this.editFormWrappers = document.querySelectorAll('div[id^="EditAddress_"]');

    this.init();
  }

  init() {
    if (this.addressNewButton) {
      this.addressNewButton.addEventListener('click', () => {
        this.toggleNewAddressForm();
      });
    }

    this.editButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const addressId = button.getAttribute('data-address-id');
        this.toggleEditAddressForm(addressId);
      });
    });
  }

  toggleNewAddressForm() {
    this.closeAllEditForms();

    let isExpanded = this.addressFormWrapper.getAttribute('aria-expanded') === 'true';
    this.addressFormWrapper.setAttribute('aria-expanded', !isExpanded);
    this.newAddressWrapper.setAttribute('aria-expanded', !isExpanded);

    if (!isExpanded) {
      const firstInput = this.newAddressWrapper.querySelector('input');
      if (firstInput) firstInput.focus();
    }
  }

  closeAllEditForms() {
    this.editButtons.forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });

    this.editFormWrappers.forEach((form) => {
      form.setAttribute('aria-expanded', 'false');
    });

    const parentEditWrapper = document.querySelector('#EditAddress');
    if (parentEditWrapper) {
      parentEditWrapper.setAttribute('aria-expanded', 'false');
    }
  }

  toggleEditAddressForm(addressId) {
    this.closeAllEditForms();
    this.closeNewAddressForm();

    // Toggle specific edit form
    const editButton = document.querySelector(`#EditFormButton_${addressId}`);
    const editFormWrapper = document.querySelector(`#EditAddress_${addressId}`);

    if (editButton && editFormWrapper) {
      const isExpanded = editButton.getAttribute('aria-expanded') === 'true';
      editButton.setAttribute('aria-expanded', !isExpanded);
      editFormWrapper.setAttribute('aria-expanded', !isExpanded);

      // Show the parent wrapper when an edit form is displayed
      const parentEditWrapper = document.querySelector('#EditAddress');
      if (parentEditWrapper) {
        parentEditWrapper.setAttribute('aria-expanded', !isExpanded);
      }

      if (!isExpanded) {
        const firstInput = editFormWrapper.querySelector('input');
        if (firstInput) firstInput.focus();
      }
    }
  }

  closeNewAddressForm() {
    if (this.addressNewButton) {
      this.addressNewButton.setAttribute('aria-expanded', 'false');
    }

    if (this.addressFormWrapper) {
      this.addressFormWrapper.setAttribute('aria-expanded', 'false');
    }
  }
}

customElements.define('customer-addresses', CustomerAddressForms);

/**
 * <responsive-order> — two-layer ordering for CLS-free layout:
 *
 *  1. CSS `order` (base.scss `responsive-order` block) applies the correct
 *     *visual* order immediately at paint time, before this element upgrades.
 *     This prevents a layout jump from "wrong source-order" → "correct order"
 *     while global.js is deferred (executes after HTML parsing).
 *
 *  2. JS `appendChild` (handleChange) corrects the real *DOM order* for tab
 *     focus and screen-reader reading order once the element has upgraded.
 *     CSS `order` is visual-only and does not affect keyboard tab sequence.
 *
 * Both layers are driven by the same `data-order-mobile` / `data-order-desktop`
 * attributes. Every direct child MUST carry both attributes; a missing attribute
 * causes CSS and JS to fall back differently (source order vs. originalOrder
 * index) and the two layers will disagree.
 *
 * The `1024px` desktop breakpoint MUST match the $desktop-breakpoint in
 * base.scss (the `responsive-order` media query). Update both together.
 */
class ResponsiveOrder extends HTMLElement {
  // 1024 must stay in sync with the `@media (min-width: 1024px)` rule in
  // base.scss that drives the CSS `order` layer (see class-level comment).
  #breakpointsMap = {
    0: 'mobile',
    1024: 'desktop',
  };

  constructor() {
    super();
    this.handleChange = this.handleChange.bind(this);
    this.originalOrder = [];
  }

  connectedCallback() {
    // Store original order
    this.originalOrder = Array.from(this.children);
    this.mediaQueryDesktop = window.matchMedia('(min-width: 1024px)');
    this.mediaQueryDesktop.addEventListener('change', this.handleChange);
    this.handleChange();
  }

  disconnectedCallback() {
    this.mediaQueryDesktop.removeEventListener('change', this.handleChange);
  }

  getCurrentBreakpoint() {
    // Return 1024 if desktop, 0 if mobile
    return this.mediaQueryDesktop.matches ? 1024 : 0;
  }

  getOrder(breakpoint, element) {
    const key = `data-order-${this.#breakpointsMap[breakpoint]}`;
    if (element.hasAttribute(key)) {
      return parseInt(element.getAttribute(key), 10);
    }

    // the original DOM order
    return this.originalOrder.indexOf(element);
  }

  handleChange() {
    const breakpoint = this.getCurrentBreakpoint();
    const children = Array.from(this.originalOrder);

    // Sort children by their order
    const sorted = children
      .map((el) => ({ el, order: this.getOrder(breakpoint, el) }))
      .sort((a, b) => a.order - b.order)
      .map((obj) => obj.el);

    // Re-append in sorted order
    sorted.forEach((el) => this.appendChild(el));

    // to prevent the animation applied twice when we change the order of dom elements
    if (this.closest('.prevent-animate-slide-in')) {
      this.closest('.prevent-animate-slide-in').classList.remove('prevent-animate-slide-in');
    }
  }
}

customElements.define('responsive-order', ResponsiveOrder);

class FeaturedProduct extends ResponsiveOrder {
  constructor() {
    super();
    this.selectedVariant = null;
    this.variantPickerSelectionSubscriber = undefined;
  }

  connectedCallback() {
    super.connectedCallback();
    this.variantPickerSelectionSubscriber = subscribe(
      PUB_SUB_EVENTS.swatchVariantPickerChange,
      this.handleVariantSelectionChange.bind(this)
    );
  }

  handleVariantSelectionChange({ selectedVariant }) {
    this.selectedVariant = selectedVariant;

    this.updateAddToCartLink();
  }

  updateAddToCartLink() {
    this.querySelector('.js-add-to-cart-cta').href = this.dataset.productUrl + '?variant=' + this.selectedVariant;
  }
}

customElements.define('featured-product', FeaturedProduct);

/**
 * Popup component that uses the Popover API when available,
 * with fallback to manual event handling for older browsers.
 *
 * @extends {HTMLElement}
 *
 * @example
 * ```html
 * <popover-component>
 *  <button ref="trigger" popovertarget="account-menu-dropdown">
 *    Trigger
 *  </button>
 *  <div ref="content" popover="auto">
 *    Content
 *  </div>
 * </popover-component>
 * ```
 */
class PopoverComponent extends HTMLElement {
  trigger = null;
  content = null;
  isOpen = false;
  cleanup = null;
  supportsPopover = false;

  constructor() {
    super();
    this.supportsPopover = Object.hasOwn(HTMLElement.prototype, 'popover');
  }

  connectedCallback() {
    this.setupElements();
    this.setupEventListeners();
  }

  disconnectedCallback() {
    this.cleanup?.();
  }

  setupElements() {
    // Find trigger and content elements
    this.trigger = this.querySelector('[ref="trigger"]');
    this.content = this.querySelector('[ref="content"]');

    if (!this.trigger || !this.content) {
      return;
    }

    // Setup based on API support
    if (this.supportsPopover) {
      this.setupPopoverAPI();
    } else {
      this.setupFallback();
    }
  }

  setupPopoverAPI() {
    // Use existing ID or generate unique ID for popover target
    let popoverId = this.content.id;
    if (!popoverId) {
      popoverId = `popup-${Math.random().toString(36).substring(2, 11)}`;
      this.content.id = popoverId;
    }

    // Configure popover
    this.content.setAttribute('popover', 'auto');

    // Only set popovertarget if not already set
    if (!this.trigger.getAttribute('popovertarget')) {
      this.trigger.setAttribute('popovertarget', popoverId);
    }

    // Listen for popover events
    this.content.addEventListener('beforetoggle', (event) => {
      this.isOpen = event.newState === 'open';
      this.updatePosition();
    });

    requestIdleCallback(() => {
      this.updatePosition();
    });
  }

  setupFallback() {
    // Hide content initially
    this.content.style.display = 'none';
    this.content.style.position = 'fixed';
    this.content.style.zIndex = '1000';

    this.setupAriaAttributes();

    // Setup trigger click handler
    this.trigger.addEventListener('click', (event) => {
      event.preventDefault();
      this.toggle();
    });
  }

  setupEventListeners() {
    // Position update on scroll/resize
    window.addEventListener('scroll', this.updatePosition.bind(this), { passive: true });
    window.addEventListener('resize', this.updatePosition.bind(this), { passive: true });
  }

  updatePosition() {
    if (!this.isOpen || !this.trigger || !this.content) return;

    const triggerRect = this.trigger.getBoundingClientRect();

    if (this.supportsPopover) {
      // Use CSS custom properties for popover positioning
      this.content.style.setProperty('--anchor-top', `${triggerRect.top}px`);
      this.content.style.setProperty('--anchor-right', `${window.innerWidth - triggerRect.right}px`);
      this.content.style.setProperty('--anchor-bottom', `${window.innerHeight - triggerRect.bottom}px`);
      this.content.style.setProperty('--anchor-left', `${triggerRect.left}px`);
    } else {
      // Direct positioning for fallback
      this.content.style.top = `0px`;
      this.content.style.right = `0px`;
      this.content.style.transform = `translate(calc(-100vw + ${triggerRect.right}px), calc(${triggerRect.bottom}px + (var(--header-height) - 4rem) / 2))`;
    }
  }

  updateAriaExpanded() {
    this.trigger.setAttribute('aria-expanded', this.isOpen.toString());
  }

  setupAriaAttributes() {
    // Ensure content has an ID for aria-controls
    let contentId = this.content.id;
    if (!contentId) {
      contentId = `popup-content-${Math.random().toString(36).substring(2, 11)}`;
      this.content.id = contentId;
    }

    // Set up ARIA attributes on trigger
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('aria-haspopup', 'menu');
    this.trigger.setAttribute('aria-controls', contentId);

    // Set up ARIA attributes on content
    this.content.setAttribute('role', 'menu');
    this.content.setAttribute('aria-labelledby', this.trigger.id || contentId + '-trigger');

    // Ensure trigger has an ID for aria-labelledby
    if (!this.trigger.id) {
      this.trigger.id = contentId + '-trigger';
    }
  }

  makeDismissable() {
    if (this.supportsPopover) return; // Popover API handles this automatically

    const handlePointerDown = (event) => {
      if (!this.content.contains(event.target) && !this.trigger.contains(event.target)) {
        this.close();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        this.close();
        this.trigger.focus();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }

  /**
   * Opens the popup
   */
  open() {
    if (this.isOpen) return;

    if (this.supportsPopover) {
      this.content.showPopover();
    } else {
      this.content.style.display = 'block';
      this.isOpen = true;
      this.updatePosition();
      this.updateAriaExpanded();
      this.cleanup = this.makeDismissable();
    }
  }

  /**
   * Closes the popup
   */
  close() {
    if (!this.isOpen) return;

    if (this.supportsPopover) {
      this.content.hidePopover();
    } else {
      this.content.style.display = 'none';
      this.isOpen = false;
      this.updateAriaExpanded();
      this.cleanup?.();
      this.cleanup = null;
    }
  }

  /**
   * Toggles the popup open/closed state
   */
  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }
}

// Register the custom element
if (!customElements.get('popover-component')) {
  customElements.define('popover-component', PopoverComponent);
}

function updateStarSignage(signage, quantity, priceElement = null) {
  if (!signage) return;
  if (signage.dataset.isGuest === 'true') return;

  const basePerUnit = parseFloat(signage.dataset.baseStars);
  const actualPerUnit = parseFloat(signage.dataset.actualStars);
  if (!basePerUnit || !actualPerUnit) return;

  const strikeThroughStars = Math.round(basePerUnit * quantity * 10) / 10;
  const actualStars = Math.round(actualPerUnit * quantity * 10) / 10;
  const hasUpgraded = actualPerUnit !== basePerUnit;

  const formatStars = (v) => (v % 1 === 0 ? String(v) : v.toFixed(1));

  const starSingular = signage.dataset.starWordSingular;
  const starsPlural = signage.dataset.starWordPlural;
  const earnedLabelSingular = signage.dataset.earnedLabelSingular;
  const earnedLabelPlural = signage.dataset.earnedLabelPlural;
  const starWord = actualStars === 1 ? starSingular : starsPlural;
  const earnedLabel = actualStars === 1 ? earnedLabelSingular : earnedLabelPlural;

  const primarySpan = signage.querySelector('.stars-signage__primary');
  if (primarySpan) {
    for (const node of primarySpan.childNodes) {
      if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
        node.textContent = earnedLabel;
        break;
      }
    }
  }

  const strikeThroughStarsElements = signage.querySelectorAll(
    '.stars-signage__base-stars, .stars-signage__base-stars-mini'
  );
  strikeThroughStarsElements.forEach((element) => {
    element.textContent = formatStars(strikeThroughStars);
  });

  if (hasUpgraded) {
    const screenReaderOriginal = primarySpan?.querySelector('.visually-hidden');
    if (screenReaderOriginal) {
      const baseWord = strikeThroughStars === 1 ? starSingular : starsPlural;
      const originalLabel = screenReaderOriginal.textContent.split(/\d/)[0].trim();
      screenReaderOriginal.textContent = `${originalLabel} ${formatStars(strikeThroughStars)} ${baseWord}, `;
    }
  }

  const actualStarsElements = signage.querySelectorAll(
    '.stars-signage__actual-stars, .stars-signage__actual-stars-mini'
  );
  actualStarsElements.forEach((actualStarsElement) => {
    const firstChild = actualStarsElement.firstChild;
    if (firstChild && firstChild.nodeType === Node.TEXT_NODE) {
      firstChild.textContent = formatStars(actualStars);
    }

    const screenReaderStarWord = actualStarsElement.nextElementSibling;
    if (screenReaderStarWord && screenReaderStarWord.classList.contains('visually-hidden')) {
      screenReaderStarWord.textContent = ` ${starWord}`;
    }
  });

  if (priceElement) {
    const unitPrice = parseInt(priceElement.dataset.unitPrice, 10);
    const formatMoney = (cents) => {
      return window.Shopify?.formatMoney?.(cents, priceElement.dataset.moneyFormat) || `$${(cents / 100).toFixed(2)}`;
    };
    if (!Number.isNaN(unitPrice)) {
      const totalPrice = unitPrice * quantity;
      priceElement.textContent = formatMoney(totalPrice);
    }
  }
}
